import {
  getPeerSelectionStatusForManager,
  getPmReviewDetails,
  getPmReviews,
  useCurrentUserId,
} from '@/modules/performance/hr-review-core';
import { useGlobalQuery } from '@/state/query-client/hooks';

/** Open peer lists routed through one manager are few; this bounds the read that counts them. */
const OPEN_PEER_REQUESTS_LIMIT = 200;

/**
 * Peer lists still waiting for `managerId`: reviews whose peer step is open and routed through them, counted
 * per manager like the Requests list does (another manager may already have chosen their peers).
 */
export const countPendingPeerRequests = async (managerId: string): Promise<number> => {
  const page = await getPmReviews({
    pageNo: 1,
    pageSize: OPEN_PEER_REQUESTS_LIMIT,
    filter: { PeerManagerIds: managerId, PeerList: { $in: ['IsRequested', 'IsForwarded'] } },
  });
  return page.items.filter(
    (review) => getPeerSelectionStatusForManager(review, managerId) === 'Pending'
  ).length;
};

/** Review forms assigned to `userId` that they haven't submitted yet. */
export const countPendingSubmissions = async (userId: string): Promise<number> => {
  const page = await getPmReviewDetails({
    pageNo: 1,
    pageSize: 1,
    filter: { PeerUserId: userId, Status: 'Pending' },
  });
  return page.totalCount;
};

/**
 * The waiting work on My Reviews' tabs (Component Map › Page tabs: tabs with waiting work show a count).
 * Each count is read only for a tab the user may open.
 */
export const useMyReviewCounts = ({
  requests,
  submissions,
}: {
  requests: boolean;
  submissions: boolean;
}) => {
  const userId = useCurrentUserId();
  const pendingRequests = useGlobalQuery({
    queryKey: ['hr-peer-requests', 'pending-count', userId],
    queryFn: () => countPendingPeerRequests(userId),
    enabled: requests && Boolean(userId),
    staleTime: 30 * 1000,
  });
  const pendingSubmissions = useGlobalQuery({
    queryKey: ['hr-my-submissions', 'pending-count', userId],
    queryFn: () => countPendingSubmissions(userId),
    enabled: submissions && Boolean(userId),
    staleTime: 30 * 1000,
  });
  return { requests: pendingRequests.data, submissions: pendingSubmissions.data };
};
